import { AsyncPipe, isPlatformBrowser } from '@angular/common';
import type { OnInit, Signal } from '@angular/core';
import {
  CUSTOM_ELEMENTS_SCHEMA,
  ChangeDetectionStrategy,
  Component,
  NgZone,
  PLATFORM_ID,
  computed,
  inject,
} from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MatIconModule, MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { Router, RouterOutlet } from '@angular/router';
import { GlobalSearchbarComponent } from '@components/molecules/global-searchbar/global-searchbar.component';
import { SelectCountryComponent } from '@components/molecules/select-country/select-country.component';
import { environment } from '@environment';
import { FeaturesRoutingEnum } from '@features/features-routing.enum';
import { DEFAULT_COUNTRY } from '@models/locale.enum';
import { RoleTypes } from '@models/roles';
import { Store } from '@ngrx/store';
import { selectCurrentCountry, selectRouteParams, selectUrl } from '@stores/router.store';
import type { NavigationItem } from '@wizbii-components/bo-angular-ui';
import {
  BoSideMenuModule,
  PageUnauthorizedModule,
  WzbBoNavigationSidebarMenuComponent,
  WzbBoNotificationsComponent,
  wzbBoNavigationSidebarMenuIconsDirective,
} from '@wizbii-components/bo-angular-ui';
import { isLogged, roles } from '@wizbii-utils/angular/stores';
import type { WebVitalsOptions, WebVitalsParams } from '@wizbii/utils';
import { sendWebVitals } from '@wizbii/utils';
import type { Observable } from 'rxjs';
import { combineLatest } from 'rxjs';
import { filter, map, switchMap, take } from 'rxjs/operators';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'hlp-core',
  imports: [
    AsyncPipe,
    RouterOutlet,
    PageUnauthorizedModule,
    WzbBoNavigationSidebarMenuComponent,
    wzbBoNavigationSidebarMenuIconsDirective,
    MatIconModule,
    WzbBoNotificationsComponent,
    ReactiveFormsModule,
    BoSideMenuModule,
    SelectCountryComponent,
    GlobalSearchbarComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './core.component.html',
  styleUrls: ['./core.component.scss'],
})
export class CoreComponent implements OnInit {
  readonly #ngZone = inject(NgZone);
  readonly matIconRegistry = inject(MatIconRegistry);
  readonly domSanitizer = inject(DomSanitizer);
  readonly #store = inject(Store);
  readonly #platformId = inject(PLATFORM_ID);
  readonly #router = inject(Router);

  readonly activeRoute = computed<string | string[]>(() => {
    const splitUrl = this.#store.selectSignal(selectUrl)()?.split('?')[0].split('/');

    if (!splitUrl) {
      return '';
    }

    return `/${splitUrl[1]}/${splitUrl[2]}`;
  });

  readonly currentCountry = this.#store.selectSignal(selectCurrentCountry);

  readonly items: Signal<NavigationItem[]> = computed(() => {
    const lang = this.currentCountry() ?? DEFAULT_COUNTRY;

    return [
      {
        route: `/${lang}/${FeaturesRoutingEnum.Orders}`,
        label: 'Commandes',
        icon: 'shopping-cart',
        grantedTo: [RoleTypes.ROLE_HELPII_MANAGER, RoleTypes.ROLE_HELPII],
      },
      {
        route: `/${lang}/${FeaturesRoutingEnum.BankingFlow}`,
        label: 'Répartition du flux',
        icon: 'slider',
        grantedTo: RoleTypes.ROLE_BANKING_FLOW_CONFIGURATOR,
      },
      {
        route: `/${lang}/${FeaturesRoutingEnum.Cco}`,
        label: 'Gestion des CCO',
        icon: 'user-circle',
        grantedTo: RoleTypes.ROLE_HELPII_MANAGER,
      },
      {
        route: `/${lang}/${FeaturesRoutingEnum.BankingPartner}`,
        label: 'Gestion des partenaires',
        icon: 'user-alt',
        grantedTo: RoleTypes.ROLE_BANKING_PARTNER_CONFIGURATOR,
      },
    ];
  });

  readonly env = environment.platform;
  get icons(): (string | undefined)[] {
    return [...this.items()].map((i) => i.icon);
  }

  get grantedRoutes$(): Observable<NavigationItem[]> {
    return this.#store.select(isLogged).pipe(
      filter((isInitialized: boolean) => isInitialized),
      take(1),
      switchMap(() => this.#store.select(roles)),
      map((userRoles) => {
        return this.items().filter((item) => {
          if (!item.grantedTo) {
            return true;
          }

          return typeof item.grantedTo === 'string'
            ? userRoles?.includes(item.grantedTo)
            : userRoles?.some((role) => (item.grantedTo ?? [])?.includes(role));
        });
      })
    );
  }

  constructor() {
    this.#ngZone.runOutsideAngular(() => {
      this.matIconRegistry.setDefaultFontSetClass('material-symbols-rounded');
      this.matIconRegistry.addSvgIconSet(
        this.domSanitizer.bypassSecurityTrustResourceUrl(`${environment.deployAssetsUrl}/sprite.svg`)
      );
    });
  }

  ngOnInit(): void {
    combineLatest([this.#store.select(selectUrl), this.#store.select(selectRouteParams)])
      .pipe(
        filter(([url, routeParams]) => !!url && !!routeParams),
        take(1)
      )
      .subscribe(([url, routeParams]) => {
        if (url) {
          const params: WebVitalsParams = {
            params: routeParams,
            path: url,
            applicationId: environment.applicationId,
            envFqdn: environment.domain.wizbii,
          };
          const options: WebVitalsOptions = {
            dev: environment.platform === 'local',
            debug: environment.platform === 'local',
            browser: isPlatformBrowser(this.#platformId),
          };
          sendWebVitals(params, options);
        }
      });
  }

  handleItemClicked(item: NavigationItem): void {
    this.#router.navigate([item.route]);
  }

  trackByIcon(index: number, icon?: string): string {
    return `${index}-${icon}`;
  }
}
